import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSecureVideoStream } from "../../../hooks/useSecureVideoStream";
import "./HeroSection.css";

const HERO_VIDEO_URL =
  "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260809_012548_ef22562c-c0ae-4816-ad9d-f8922af4e6a7.mp4";

export const HeroSection = () => {
  const heroContainerRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Secure Video Stream with Chunked Blob Delivery & Viewport Intersection Auto-Pause
  const { videoProps } = useSecureVideoStream(videoRef, {
    src: HERO_VIDEO_URL,
    containerRef: heroContainerRef,
  });

  // Count up logic
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const statElements = document.querySelectorAll(".stat-value");
          statElements.forEach((el, i) => {
            const targetStr = el.getAttribute("data-target");
            if (!targetStr) return;
            const target = Number.parseFloat(targetStr);
            const decimals = Number.parseInt(el.getAttribute("data-decimals") || "0", 10);
            const suffix = el.getAttribute("data-suffix") || "";
            const duration = 1500 + i * 80;
            const delay = 480 + i * 90;

            const startTime = performance.now() + delay;
            const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

            const animate = (currentTime: number) => {
              if (currentTime < startTime) {
                requestAnimationFrame(animate);
                return;
              }
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);
              const eased = easeOutCubic(progress);

              const currentVal = target * eased;
              el.textContent = currentVal.toFixed(decimals) + suffix;

              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                el.textContent = target.toFixed(decimals) + suffix;
              }
            };
            requestAnimationFrame(animate);
          });
        }
      },
      { threshold: 0.25 },
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
    return () => observer.disconnect();
  }, [hasAnimated]);

  return (
    <div className="hero-page" ref={heroContainerRef} onContextMenu={(e) => e.preventDefault()}>
      <video
        ref={videoRef}
        className="bg-video"
        {...videoProps}
      />

      {/* Atmospheric Contrast Scrim & Color Grading */}
      <div className="hero-scrim" aria-hidden="true" />

      {/* Hero Content */}
      <main className="hero-content">


        <h1 className="headline anim" style={{ "--d": "0s" } as React.CSSProperties}>
          <span style={{ animationDelay: "0.12s" }}>Validation</span>
          <span style={{ animationDelay: "0.3s" }}>Built For AI Teams</span>
        </h1>

        <p className="subhead anim" style={{ "--d": "0.28s" } as React.CSSProperties}>
          Independent expert validation with multi-reviewer consensus, automated agreement scoring, and audit-ready quality reports.
        </p>

        <button type="button" className="cta-btn anim-pulse" style={{ "--d": "0.4s" } as React.CSSProperties} onClick={() => window.location.href = '/user/signup/'}>
          Get Started
        </button>
      </main>
    </div>
  );
};
